import { createApp } from 'vue';
import { pushTelemetryEvent, flushTelemetry } from './core/telemetry';
import { initFaro, deriveFaroConfig } from './core/faro';
import { isSyntheticAgent } from './core/syntheticAgent';
import { resolveFaroCollectorHost } from './config/hosts';
import { resolveParentOrigin } from './utils/resize';
// The widget's baked `<branch>-<sha7>` build identity — a build-time constant, see the
// module. Needed HERE as well as in app.vue: this file owns the degraded path, and until it
// read the constant that path reported `app_version=dev` for every mount failure.
import { BUILD_VERSION } from './config/buildVersion';
// Self-hosted Roboto (P2-9): a single global @font-face with a base64-inlined
// latin woff2. Imported here (once, at the entry) so it ships in the frozen main
// bundle and applies globally — an @font-face must not live in a scoped SFC
// <style>, and duplicating it per component would duplicate the base64 blob.
import './assets/roboto-latin.css';
// D-233 / plan §3.3: the shared `:focus-visible` ring. Imported HERE, at the entry,
// for the same reason the font is — one global copy that reaches all four player
// versions, rather than eight scoped copies that reach only v3 and v4. See the file.
import './assets/a11y.css';
import App from './app.vue';

// P3-3 (MR-31): mount the real app.vue under Vue 3 (was the Phase-2 stub).
// app.vue owns the resize handshake — its mounted() wires the window-resize
// listener and posts the origin-checked {id,type:'resize',height,width} to the
// parent (reading the widget UUID from window.name itself). So this entry no
// longer runs the stub-era handshake; utils/resize stays for stub-resize.spec
// and the MR-36 resize-under-Vue3 work. Guarded on #app so importing this module
// stays side-effect-free (and export-free → the Vite IIFE leaks no global).
function bootstrap() {
  // Mount target, newest first. The iframe html (P2-11) uses `<div id="app">`,
  // but embeds cached from BEFORE the cutover still hold the frozen document
  // whose body is `<app></app>` — and prod serves that html with NO
  // Cache-Control and a 2025-01-23 Last-Modified, so browsers keep it under
  // heuristic freshness. Meanwhile scripts/static.sh republishes each new bundle
  // onto the unhashed `futuri-widget-main.js` key that the OLD html requests, so
  // "old document + new bundle" is a REACHABLE combination at the cutover, not a
  // hypothetical one. Looking up `#app` alone made that combination render an
  // empty iframe with no console error, no Faro event and no 404 — invisible,
  // and invisible on widgetv2-dev too, which has only ever served the new html.
  const mountEl = document.getElementById('app')
    || document.getElementsByTagName('app')[0];

  if (!mountEl) {
    // Neither target present means the document is not one we ship. Say so:
    // a silent return is exactly how PS-22 and this defect both stayed hidden.
    //
    // ⚠️ console.error alone is NOT observability, and !221 overclaimed when it
    // said otherwise (independent review, LOW). Faro is configured
    // `captureConsole: false` — deliberately, since this is a third-party host
    // page — and Faro initializes inside app.vue's mount path, i.e. the path
    // that just failed. So a console line here is visible only to someone with
    // devtools open on someone else's website.
    //
    // 🪤 The FIRST fix for that was itself inert, and independent review
    // MEASURED it: `pushTelemetryEvent` buffers into a module-scoped array, and
    // the only `flushTelemetry()` call in `src/` is in app.vue's `mounted()` —
    // a hook that by construction cannot run when the mount target is missing.
    // The event was pushed into an array in a dead iframe and never sent. A
    // "now observable" claim that delivers nothing is worse than the console
    // line it replaced, because it stops anyone looking again.
    //
    // So this path brings up Faro ITSELF and drains the buffer. Everything it
    // needs is derivable without app state: the collector is a function of the
    // API host, and on this path there is no parsed config, so the default host
    // is the right (and only) answer. Bot-gated exactly as app.vue is, wrapped
    // so a telemetry failure can never surface to the embed, and terminal — the
    // widget is already dead, nothing downstream depends on it.
    // eslint-disable-next-line no-console
    console.error(
      '[futuri-widget] no mount target: the iframe document has neither #app nor <app>, '
      + 'so the widget cannot start'
    );
    // 🔑 THE ORIGIN, NOT THE HREF — and this line used to be the href.
    //
    // The actionable datum is WHICH embed is broken, and the origin answers that.
    // The full href does not just answer it more precisely, it also carries the
    // host page's query string, which the loader copies verbatim into the iframe
    // URL — session tokens, email addresses, whatever the customer's CMS puts
    // there. core/telemetry.js declines to stamp the href for exactly that
    // reason; this call site was shipping it anyway, on a live delivery path,
    // one module away. Same field name, same question, opposite answer.
    //
    // Resolved with the SAME helper as everywhere else. This runs before app.vue
    // mounts (that is the failure being reported), so `parentOrigin` does not
    // exist yet and the iframe's own search string is the input.
    const hostSearch = (typeof window !== 'undefined' && window.location)
      ? window.location.search : '';
    const hostReferrer = typeof document !== 'undefined' ? document.referrer : '';
    const hostOrigin = resolveParentOrigin(hostSearch, hostReferrer);
    pushTelemetryEvent('widget_mount_failed', {
      reason: 'no_mount_target',
      host_origin: hostOrigin,
    });
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      if (!isSyntheticAgent(ua)) {
        // Same rule as app.vue: the collector follows the SERVING origin. This is the
        // degraded path (the app did not mount), which is exactly when knowing WHICH
        // environment is broken matters — reporting it to production would file avow's
        // failure under prod.
        const collectorHost = resolveFaroCollectorHost(
          typeof window !== 'undefined' && window.location ? window.location.hostname : ''
        );
        // 🔴 BUILD_VERSION, not `undefined`. `deriveFaroConfig` returns
        // `version: buildHash || 'dev'`, so passing nothing filed the one stream that says
        // "this embed failed to mount" — plus the whole exception stream on a path where
        // app.vue never ran — under `app_version=dev`. `3b6b413` baked the identity into
        // app.vue's call site and missed this, the second of two. The cutover state this
        // path exists to report ("old document + new bundle", see the comment above) is
        // precisely one where WHICH BUILD is the question being asked.
        const { faroUrl, environment, version } = deriveFaroConfig(collectorHost, BUILD_VERSION);
        if (faroUrl) {
          // hostOrigin travels into initFaro, not just onto the event above. This is
          // the degraded path — the app did not mount — so what it reports is mostly
          // NOT hand-pushed events: it is the exception stream and whatever
          // web-vitals fire. Stamping only the one event we push by hand would
          // reproduce the ~20-of-22 shape (see core/faro.js) on the one path where
          // the diagnostic value of "which embedder is this happening on" is highest.
          initFaro(faroUrl, { version, environment, hostOrigin });
          flushTelemetry();
        }
      }
    } catch (e) {
      /* observability must never surface to the embed */
    }
    return undefined;
  }

  // href source contract: the current iframe html stamps `href` on `#app`; the
  // legacy one stamps it on `<app>` via its own inline script. Either way it is
  // read off the element we actually mounted into — reading it from the wrong
  // node would silently render with the wrong config, which looks like success.
  const href = mountEl.getAttribute('href') || document.location.href;
  const app = createApp(App, { href });
  // Mount the ELEMENT, not the '#app' selector — the legacy target is a tag, so
  // a selector mount would find nothing and fail exactly where we just recovered.
  app.mount(mountEl);

  return app;
}

bootstrap();
